import { legacyCreateColumnHelper as createColumnHelper } from '@tanstack/react-table/legacy'
import { useMemo, useState } from 'react'

import { DataTable } from '@/components/data-table/DataTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryState } from '@/hooks/use-query-state'
import { useTablePreferences } from '@/hooks/use-table-preferences'
import { formatCubeDateTime, formatInteger, formatText } from '@/lib/format'
import { cubeWallClock } from '@/lib/hr'
import { messages } from '@/lib/messages'
import {
  DUZELTME_MEMBERS,
  HR_VIEWS,
  IZIN_MEMBERS,
  hrFilters,
  type DuzeltmeRow,
  type IzinRow,
} from '@/services/cube/hr'
import { CategoryBadge } from '@/features/hr/AttendancePage'
import { hourFixable } from '@/features/hr/action-specs'
import { HourFixDialog } from '@/features/hr/conflicts'
import { HrFilterBar } from '@/features/hr/list'
import { useHrCubeFilters, useHrList } from '@/features/hr/use-hr-list'
import {
  ActionStatusBadge,
  DetailGrid,
  PersonCell,
  RecordActions,
  RecordHistory,
  RecordStatusBadge,
  SubRowLayout,
  type ActionSpec,
} from '@/features/hr/parts'
import { useLatestActions } from '@/features/hr/hr-actions'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import { RequestFormSheet, type RequestFormTarget } from '@/features/hr/RequestForm'
import type { LeaveValues } from '@/features/hr/request-value'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const l = t.leaves
const VIEW = HR_VIEWS.izin
const column = createColumnHelper<IzinRow>()

const STATUS_OPTIONS = ['bekliyor', 'hatali', 'aktarildi', 'atlandi', 'silindi', 'yoksayildi']
const CATEGORY_OPTIONS = [
  'personel_yok',
  'firma_config',
  'donem_kodu',
  'izin_turu_eslesmesi',
  'sure_hesaplanamadi',
  'tarih_hatali',
  'dia_hatasi',
]
/** İzin çakışmaları İşlerim'e taşındı (`/ik/islerim?sekme=izin_cakismasi`). */
type Tab = 'list' | 'fixes'

function leaveActions(row: IzinRow, onHourFix: (row: IzinRow) => void): ActionSpec[] {
  const actions: ActionSpec[] = [
    { type: 'izin_tekrar_gonder' },
    { type: 'izin_passgage_guncel_al' },
  ]
  if (hourFixable(row)) actions.push({ type: 'izin_saat_duzelt', onSelect: () => onHourFix(row) })
  actions.push(row.karar ? { type: 'karar_kaldir' } : { type: 'izin_yoksay', needsReason: true })
  return actions
}

/** İznin talep biçimindeki hali (Cube duvar saatinden): tam günde gün, saatlikte gün + saat. */
function leaveCurrent(row: IzinRow): LeaveValues | null {
  const start = cubeWallClock(row.baslangic)
  const end = cubeWallClock(row.bitis)
  if (!start || !end || !row.izinTuru) return null
  const hourly = row.saatlik === true
  return {
    tur: row.izinTuru,
    saatlik: hourly,
    baslangic: hourly ? `${start.date} ${start.time}` : start.date,
    bitis: hourly ? `${end.date} ${end.time}` : end.date,
  }
}

/** Talep izni olana "İzni güncelle" / "İzni iptal et": onaydan sonra Passgage'e, oradan izin süreciyle DİA'ya. */
function LeaveRequestButton({ row }: { row: IzinRow }) {
  const canCreate = useAuthStore((state) => state.hasPermission('hr.request:create'))
  const [target, setTarget] = useState<RequestFormTarget | null>(null)
  const current = leaveCurrent(row)
  if (!canCreate || !current || !row.sicilno || row.durum === 'silindi') return null
  return (
    <>
      {(['izin_guncelle', 'izin_iptal'] as const).map((kind) => (
        <Button
          key={kind}
          size="sm"
          variant="secondary"
          className="w-fit"
          onClick={() =>
            setTarget({
              kind,
              sicilno: row.sicilno ?? undefined,
              name: row.adisoyadi,
              leaveId: row.id,
              leave: current,
            })
          }
        >
          {kind === 'izin_iptal' ? t.approvals.cancelLeave.open : t.approvals.leave.open}
        </Button>
      ))}
      <RequestFormSheet target={target} onClose={() => setTarget(null)} />
    </>
  )
}

function LeaveDetail({ row, onHourFix }: { row: IzinRow; onHourFix: (row: IzinRow) => void }) {
  return (
    <SubRowLayout
      detail={
        <>
          <DetailGrid
            items={[
              [t.detail.fields.izinTuru, formatText(row.izinTuru)],
              [t.detail.fields.baslangic, formatCubeDateTime(row.baslangic)],
              [t.detail.fields.bitis, formatCubeDateTime(row.bitis)],
              [t.detail.fields.saatlik, row.saatlik ? t.yes : t.no],
              [
                t.detail.fields.sure,
                row.sureDakika === null ? null : t.detail.durationMinutes(row.sureDakika),
              ],
              [t.detail.fields.aciklama, formatText(row.aciklama)],
              [t.detail.fields.sicil, formatText(row.sicilno)],
              [t.detail.fields.firma, formatText(row.firma)],
              [t.filters.status, <RecordStatusBadge key="s" status={row.durum} />],
              [t.filters.category, <CategoryBadge key="c" category={row.hataKategori} />],
              [t.detail.error, formatText(row.sonHata)],
              [t.detail.attempts, formatInteger(row.denemeSayisi)],
              [t.detail.fields.diaIzinKey, formatText(row.diaIzinKey)],
              ...(row.karar
                ? ([
                    [
                      t.detail.decision,
                      [
                        row.kararAciklama,
                        row.kararKullanici && t.detail.decisionBy(row.kararKullanici),
                      ]
                        .filter(Boolean)
                        .join(' — '),
                    ],
                  ] as [string, string][])
                : []),
            ]}
          />
          {row.saatlik === false && (
            <p className="text-text-muted text-xs">{t.hourFix.notHourly}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <RecordActions kind="izin" id={row.id} actions={leaveActions(row, onHourFix)} />
            <LeaveRequestButton row={row} />
          </div>
        </>
      }
      history={<RecordHistory kind="izin" id={row.id} />}
    />
  )
}

function LeaveList({ onHourFix }: { onHourFix: (row: IzinRow) => void }) {
  const query = useQueryState()
  const focusId = query.get('id')
  const filters = useHrCubeFilters(VIEW, {
    statusMember: 'durum',
    categoryMember: 'hataKategori',
    dateMember: 'baslangic',
    extra: focusId ? [{ member: `${VIEW}.id`, operator: 'equals', values: [focusId] }] : [],
  })
  const list = useHrList<IzinRow>({
    view: VIEW,
    members: IZIN_MEMBERS,
    rowKey: 'id',
    filters,
    defaultSort: [{ id: 'baslangic', desc: true }],
  })
  const actions = useLatestActions(
    'izin',
    list.rows.map((row) => row.id),
    VIEW,
  )
  const preferences = useTablePreferences('ikizin', {})
  // Hareketten gelen bağlantı (`?id=`) izni açık getirir.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    focusId ? { [focusId]: true } : {},
  )

  const columns = useMemo(
    () => [
      column.accessor((row) => row.adisoyadi, {
        id: 'adisoyadi',
        header: l.columns.ad,
        size: 220,
        cell: (info) => <PersonCell name={info.getValue()} sub={info.row.original.sicilno} />,
      }),
      column.accessor((row) => row.izinTuru, {
        id: 'izinTuru',
        header: l.columns.tur,
        cell: (info) => formatText(info.getValue()),
      }),
      column.accessor((row) => row.baslangic, {
        id: 'baslangic',
        header: l.columns.baslangic,
        size: 160,
        cell: (info) => formatCubeDateTime(info.getValue()),
      }),
      column.accessor((row) => row.bitis, {
        id: 'bitis',
        header: l.columns.bitis,
        size: 160,
        cell: (info) => formatCubeDateTime(info.getValue()),
      }),
      column.accessor((row) => (row.saatlik ? t.yes : t.no), {
        id: 'saatlik',
        header: l.columns.saatlik,
        size: 80,
      }),
      column.accessor((row) => row.durum, {
        id: 'durum',
        header: l.columns.durum,
        size: 170,
        cell: (info) => <RecordStatusBadge status={info.getValue()} />,
        meta: { exportValue: (row: IzinRow) => t.recordStatus[row.durum] ?? row.durum },
      }),
      column.accessor((row) => row.sonHata, {
        id: 'sonHata',
        header: l.columns.hata,
        cell: (info) => formatText(info.getValue()),
      }),
      column.display({
        id: 'aksiyon',
        header: l.columns.aksiyon,
        size: 120,
        cell: (info) => <ActionStatusBadge action={actions.get(info.row.original.id)} />,
        meta: { exportable: false },
      }),
    ],
    [actions],
  )

  return (
    <div className="flex flex-col gap-4">
      <HrFilterBar
        view={VIEW}
        idPrefix="hr-izin"
        withDates
        extraKeys={['id']}
        optionFilters={[
          {
            key: 'durum',
            label: t.filters.status,
            options: STATUS_OPTIONS.map((value) => ({ value, label: t.recordStatus[value] })),
          },
          {
            key: 'kategori',
            label: t.filters.category,
            options: CATEGORY_OPTIONS.map((value) => ({ value, label: t.category[value] })),
          },
        ]}
      />
      <DataTable
        tableId="ikizin"
        title={l.title}
        columns={columns}
        data={list.rows}
        total={list.total}
        getRowId={(row) => row.id}
        sorting={list.sorting}
        onSortingChange={list.onSortingChange}
        pagination={list.pagination}
        onPaginationChange={list.setPagination}
        isLoading={list.isLoading}
        busy={list.busy}
        isError={list.isError}
        error={list.error}
        onRetry={list.refetch}
        layout={preferences.layout}
        onLayoutChange={preferences.save}
        onLayoutReset={preferences.reset}
        expanded={expanded}
        onExpandedChange={setExpanded}
        renderSubRow={(row) => <LeaveDetail row={row} onHourFix={onHourFix} />}
      />
    </div>
  )
}

const fixColumn = createColumnHelper<DuzeltmeRow>()

/** `izin_saat_duzeltme` logu; tutarsız satır "Passgage'i DİA ile hizala" ile onarılır (İ6). */
function FixList() {
  const view = HR_VIEWS.duzeltme
  const [onlyOpen, setOnlyOpen] = useState(true)
  const filters = hrFilters({
    view,
    equals: { sonuc: onlyOpen ? ['tutarsiz', 'hata'] : [] },
  })
  const list = useHrList<DuzeltmeRow>({
    view,
    members: DUZELTME_MEMBERS,
    rowKey: 'id',
    filters,
    defaultSort: [{ id: 'olusturma', desc: true }],
  })
  const preferences = useTablePreferences('ikizinduzeltme', {})
  const columns = useMemo(
    () => [
      fixColumn.accessor((row) => row.olusturma, {
        id: 'olusturma',
        header: l.fixes.columns.zaman,
        size: 160,
        cell: (info) => formatCubeDateTime(info.getValue()),
      }),
      fixColumn.accessor((row) => row.adisoyadi, {
        id: 'adisoyadi',
        header: l.fixes.columns.ad,
        size: 200,
        cell: (info) => <PersonCell name={info.getValue()} sub={info.row.original.sicilno} />,
      }),
      fixColumn.accessor((row) => row.izinTuru, {
        id: 'izinTuru',
        header: l.fixes.columns.tur,
        cell: (info) => formatText(info.getValue()),
      }),
      fixColumn.accessor(
        (row) => `${formatCubeDateTime(row.eskiBas)} – ${formatCubeDateTime(row.eskiBit)}`,
        {
          id: 'eski',
          header: l.fixes.columns.eski,
          size: 260,
          enableSorting: false,
        },
      ),
      fixColumn.accessor(
        (row) => `${formatCubeDateTime(row.yeniBas)} – ${formatCubeDateTime(row.yeniBit)}`,
        {
          id: 'yeni',
          header: l.fixes.columns.yeni,
          size: 260,
          enableSorting: false,
        },
      ),
      fixColumn.accessor((row) => row.sonuc, {
        id: 'sonuc',
        header: l.fixes.columns.sonuc,
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return null
          const tone =
            value === 'basarili' ? 'success' : value === 'tutarsiz' ? 'warning' : 'danger'
          return (
            <Badge size="sm" tone={tone}>
              {l.fixes.result[value] ?? value}
            </Badge>
          )
        },
      }),
      fixColumn.accessor((row) => row.hata, {
        id: 'hata',
        header: l.fixes.columns.hata,
        cell: (info) => formatText(info.getValue()),
      }),
    ],
    [],
  )
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-text-secondary text-sm">{l.fixes.description}</p>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={onlyOpen} onCheckedChange={setOnlyOpen} />
          {l.fixes.onlyOpen}
        </label>
      </div>
      <DataTable
        tableId="ikizinduzeltme"
        title={l.tabs.fixes}
        columns={columns}
        data={list.rows}
        total={list.total}
        getRowId={(row) => row.id}
        sorting={list.sorting}
        onSortingChange={list.onSortingChange}
        pagination={list.pagination}
        onPaginationChange={list.setPagination}
        isLoading={list.isLoading}
        busy={list.busy}
        isError={list.isError}
        error={list.error}
        onRetry={list.refetch}
        layout={preferences.layout}
        onLayoutChange={preferences.save}
        onLayoutReset={preferences.reset}
        renderSubRow={(row) => (
          <SubRowLayout
            detail={
              <>
                <DetailGrid
                  items={[
                    [t.detail.fields.firma, formatText(row.firma)],
                    [
                      t.detail.fields.tip,
                      t.girisCikis[row.hareketTuru ?? ''] ?? formatText(row.hareketTuru),
                    ],
                    [t.detail.fields.zaman, formatCubeDateTime(row.hareketZamani)],
                    [t.detail.error, formatText(row.hata)],
                  ]}
                />
                {row.sonuc === 'tutarsiz' && (
                  <RecordActions
                    kind="izin_duzeltme"
                    id={row.id}
                    actions={[{ type: 'izin_duzeltme_onar' }]}
                  />
                )}
              </>
            }
            history={<RecordHistory kind="izin_duzeltme" id={row.id} />}
          />
        )}
      />
    </div>
  )
}

export function LeavesPage() {
  const query = useQueryState()
  const tab: Tab = query.get('sekme') === 'fixes' ? 'fixes' : 'list'
  const [hourFix, setHourFix] = useState<IzinRow | null>(null)
  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader title={l.title} description={l.description} />
      <Tabs
        value={tab}
        onValueChange={(value: Tab) => query.set({ sekme: value === 'list' ? undefined : value })}
      >
        <TabsList>
          <TabsTrigger value="list">{l.tabs.list}</TabsTrigger>
          <TabsTrigger value="fixes">{l.tabs.fixes}</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="pt-3">
          <LeaveList onHourFix={setHourFix} />
        </TabsContent>
        <TabsContent value="fixes" className="pt-3">
          <FixList />
        </TabsContent>
      </Tabs>
      <HourFixDialog key={hourFix?.id ?? 'none'} leave={hourFix} onClose={() => setHourFix(null)} />
    </div>
  )
}
